import { gql } from '@apollo/client'
import { orderCheckoutFragment } from '../graphql'
import { useMutation, useShippingAddressValidation } from '@ztechapps/common.hook'
import isEmpty from 'lodash/isEmpty'
import { useCheckoutContext } from '../index'
import { toDate, validatePhoneNumber } from '@ztechapps/common.utils'

const validateCarrierQuery = gql`
  ${orderCheckoutFragment}
  mutation validateCarrierAccount($input: JSON!) {
    validateCarrierAccount(input: $input) {
      _id
      ...orderCheckoutFragment
    }
  }
`

export const useValidateShipment = (): {
  validateShippingOptions: () => boolean | string[]
  parseError: (errorMessage: string) => string
  validate: () => Promise<void>
} => {
  const context = useCheckoutContext()
  const { document } = context.orderDocumentResult
  const [shippingOptions] = context.shippingOptions
  const addressValidation = useShippingAddressValidation()

  const [onValidateCarrier] = useMutation({ query: validateCarrierQuery })

  const validateShippingOptions = () => {
    const errors: string[] = []

    if (!shippingOptions?.shippingAddress?.street1) errors.push('Please set the shipping address (Address Line 1)') //throw new Error('Please set the shipping address (Address Line 1)')
    if (!shippingOptions?.shippingAddress?.city) errors.push('Please set the shipping address (City/District)') //throw new Error('Please set the shipping address (City/District)')
    if (!shippingOptions?.shippingAddress?.state) errors.push('Please set the shipping address (State/Province/Region)') //throw new Error('Please set the shipping address (State/Province/Region)')
    if (!shippingOptions?.shippingAddress?.zip) errors.push('Please set the shipping address (ZIP/Postal Code)') //  throw new Error('Please set the shipping address (ZIP/Postal Code)')
    if (!shippingOptions?.shippingAddress?.country) errors.push('Please set the shipping address (Country)') //throw new Error('Please set the shipping address (Country)')
    if (!isEmpty(shippingOptions.shippingAddress?.errors)) errors.push('Please set a valid shipping address.') //throw new Error('Please set a valid shipping address.')

    //validate street1
    try {
      addressValidation?.validate({ address: shippingOptions?.shippingAddress?.street1 })
    } catch (e) {
      errors.push(e?.message)
    }

    if (!shippingOptions?.name) errors.push('Please set "Your Name" as the recipient for this shipment.') //throw new Error('Please set "Your Name" as the recipient for this shipment.')

    if (!shippingOptions?.companyName) errors.push('Please set "Company Name" as the recipient for this shipment.') //throw new Error('Please set "Company Name" as the recipient for this shipment.')

    if (isEmpty(shippingOptions?.phone)) {
      errors.push('Phone number is required so we can easily contact you if there are issues with the shipment.')
    } else {
      try {
        validatePhoneNumber(shippingOptions?.phone)
      } catch (e) {
        errors.push(e.message)
      }
    }

    //if the phone number has value and less than 10 characters then throw error
    if (shippingOptions?.phone && (shippingOptions?.phone?.length ?? 0) < 10)
      errors.push('Phone number must be more than 10 characters.') //throw new Error('Phone number must be more than 10 characters.')

    //validate the shipping date
    if (!shippingOptions?.shipmentDate) {
      errors.push('Please set "Shipping date"')
    } else {
      const shipmentDate = toDate(shippingOptions?.shipmentDate)
      if (shipmentDate.isBefore(toDate(), 'day')) {
        errors.push('Shipping date cannot be in the past.')
      }
    }

    if (!isEmpty(errors)) {
      return errors
    }

    return true
  }

  const validate = async () => {
    const validationResult = await onValidateCarrier({ input: { _id: document?._id, orderId: document?._id } })
    const serviceErrors = validationResult?.data?.validateCarrierAccount?.shipping?.serviceErrors

    if (!isEmpty(serviceErrors)) {
      const errorMessages = []
      for (const error of serviceErrors ?? []) {
        const result = parseErrors(error)
        errorMessages.push(result)
      }

      //need to remove the serviceErrors since it is only used to validate Order
      throw new Error(errorMessages?.join(`\n`))
    }
  }

  return { validate, parseError: parseErrors, validateShippingOptions }
}

export const parseErrors = (errorMessage: string): string => {
  let result = errorMessage.substring(errorMessage.indexOf('message') - 1)
  result = result.substring(12)
  result = result.substring(0, result.indexOf('"'))
  return result
}
